import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { navLinks } from '@/mocks/homeData';
import ContactModal from '@/components/feature/ContactModal';
import { getSiteConfig } from '@/utils/siteConfigStore';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'consultation' | 'franchise'>('consultation');
  const siteConfig = getSiteConfig();

  const handleNavClick = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const openModal = (type: 'consultation' | 'franchise') => {
    setModalType(type);
    setModalOpen(true);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/" onClick={handleNavClick('/')} className="flex items-center cursor-pointer">
            <img
              src={scrolled ? siteConfig.logoUrlDark : siteConfig.logoUrl}
              alt="麦杰集团"
              className="h-10 md:h-11 w-auto object-contain"
            />
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={handleNavClick(link.path)}
                className={`relative text-sm font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer ${
                  scrolled
                    ? isActive(link.path) ? 'text-brand-deep' : 'text-gray-600 hover:text-brand-deep'
                    : isActive(link.path) ? 'text-white' : 'text-white/80 hover:text-white'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full transition-colors duration-300 ${
                    scrolled ? 'bg-brand-warm' : 'bg-white'
                  }`} />
                )}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => openModal('consultation')}
              className={`px-5 py-2 text-sm font-medium rounded-full border whitespace-nowrap transition-all duration-300 cursor-pointer ${
                scrolled
                  ? 'border-brand-warm text-brand-dark hover:bg-brand-light'
                  : 'border-white/60 text-white hover:bg-white/10'
              }`}
            >
              预约体验
            </button>
            <button
              onClick={() => openModal('franchise')}
              className="px-5 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-300 cursor-pointer bg-brand-deep text-white hover:bg-brand-dark"
            >
              立即加盟
            </button>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
              scrolled ? 'text-brand-deep' : 'text-white'
            }`}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => { handleNavClick(link.path)(e); setMobileOpen(false); }}
                className={`text-sm font-medium py-2 transition-colors cursor-pointer ${
                  isActive(link.path) ? 'text-brand-deep' : 'text-gray-600 hover:text-brand-deep'
                }`}
              >
                {link.label}
              </a>
            ))}
            <div className="flex gap-3 pt-3 border-t border-gray-100">
              <button
                onClick={() => { openModal('consultation'); setMobileOpen(false); }}
                className="flex-1 text-center px-4 py-2 text-sm font-medium rounded-full border border-brand-warm text-brand-dark cursor-pointer"
              >
                预约体验
              </button>
              <button
                onClick={() => { openModal('franchise'); setMobileOpen(false); }}
                className="flex-1 text-center px-4 py-2 text-sm font-medium rounded-full bg-brand-deep text-white cursor-pointer"
              >
                立即加盟
              </button>
            </div>
          </div>
        </div>
      )}
      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} type={modalType} />
    </nav>
  );
}